import React, { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import moment from 'moment'
import NumberFormat from 'react-number-format'
import { useTranslation } from 'react-i18next'

import {
  Autocomplete,
  Dialog,
  CircularProgress,
  Typography,
  Accordion as MuiAccordion,
  AccordionSummary as MuiAccordionSummary,
  AccordionDetails as MuiAccordionDetails,
  IconButton,
  Button,
  Tooltip,
  DialogTitle,
  DialogContent,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material'
import { Cancel } from '@material-ui/icons'
import { styled } from '@mui/material/styles'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import TextField from "@material-ui/core/TextField"

import CoafDateRangePicker from './CoafDateRangePicker'
import CoafMoneyTextField from './CoafMoneyTextField'
import CoafClauseNumberList from './CoafClauseNumberList'
import OccurrenceInvolvedPartiesTable from './OccurrenceInvolvedPartiesTable'
import { validateCNPJ, validateCPF } from "../utils/utils"

const Accordion = styled((props) => (
    <MuiAccordion disableGutters elevation={0} square {...props} />
))(() => ({
    border: 'none',
    '&:before': {
        display: 'none',
    },
}))

const AccordionSummary = styled((props) => (
    <MuiAccordionSummary
        expandIcon={<ExpandMoreIcon />}
        {...props}
    />
))(({ theme }) => ({
    borderBottom: `2px solid ${theme.palette.divider}`,
    padding: 0,
    flexDirection: 'row',
    '& .MuiAccordionSummary-content': {
        marginLeft: 0,
    },
}))

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
    padding: theme.spacing(2)
}))

const SectionHeader = styled('div')(({ theme }) => ({
    marginTop: theme.spacing(3),
    borderBottom: `2px solid ${theme.palette.divider}`,
    minHeight: '48px',
    display: 'flex',
    alignItems: 'center',
}))

function CoafOccurrenceDialog(props) {
    const { t } = useTranslation()
    const { occurrence, occurrenceDialogState, setOccurrenceDialogState, setSnackbarContent, refreshList } = props
    const { open, mode } = occurrenceDialogState

    const [occurrenceData, setOccurrenceData] = useState(null)
    const [expanded, setExpanded] = useState({
        transactions: false,
        parties: true,
    })
    const [isLoading, setIsLoading] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const [errorState, setErrorState] = useState({})
    const [confirmExitOpen, setConfirmExitOpen] = useState(false)

    const trimNestedObject = (input) => {
        const isPlainObject = (val) => (
            val !== null &&
            Object.prototype.toString.call(val) === "[object Object]" &&
            (Object.getPrototypeOf(val) === Object.prototype || Object.getPrototypeOf(val) === null)
        )

        if (!(Array.isArray(input) || isPlainObject(input))) return input

        const root = Array.isArray(input) ? [] : {}
        const stack = [{ source: input, target: root }]

        const assignValue = (container, key, value) => {
            if (typeof value === "string") {
                container[key] = value.trim()
            } else if (Array.isArray(value)) {
                const next = []
                container[key] = next
                stack.push({ source: value, target: next })
            } else if (isPlainObject(value)) {
                const next = {}
                container[key] = next
                stack.push({ source: value, target: next })
            } else if (value === null || value === undefined) {
                delete container[key]
            } else {
                container[key] = value
            }
        }

        while (stack.length) {
            const { source, target } = stack.pop()

            if (Array.isArray(source)) {
                source.forEach((value, index) => assignValue(target, index, value))
            } else {
                Object.entries(source).forEach(([key, value]) => assignValue(target, key, value))
            }
        }
        return root
    }

    const sanitizeParty = (party, isCvm) => {
        const { _uiId, ...rest } = party || {}
        if (!isCvm) return rest

        const {
            branch_number,
            branch_name,
            account_number,
            account_open_date,
            register_update_date,
            no_document,
            ...restParty
        } = rest

        const sanitized = { ...restParty }
        if (no_document) {
            delete sanitized.document_number
        }
        return sanitized
    }

    const sanitizeOccurrenceData = (data) => {
        const { involved_parties, ...rest } = data || {}
        const isCvm = rest.regulatory_authority === 'cvm'

        const sanitizedParties = involved_parties?.map(party => sanitizeParty(party, isCvm))

        let payload = { ...rest, involved_parties: sanitizedParties }
        if (isCvm) {
            const {
                branch_number,
                branch_name,
                debit_amount,
                provisioning_amount,
                proposal_amount,
                ...restPayload
            } = payload
            payload = restPayload
        }
        const trimmedPayload = trimNestedObject(payload)
        return trimmedPayload
    }

    const generateKey = () => Math.random().toString(36).substring(2, 15)

    const hidrateOccurrenceDataKeys = useCallback((data) => {
        const {involved_parties, ...rest} = data || {}

        const partiesWithKey = involved_parties?.map(party => {
            return { ...party, _uiId: generateKey() }
        })

        return {...rest, involved_parties: partiesWithKey}
    }, [])

    const handleClose = () => {
        setOccurrenceDialogState({ open: false, mode: "view", occurrence: null })
        setOccurrenceData(null)
        setErrorState({})
        setConfirmExitOpen(false)
        if (refreshList) refreshList()
    }

    const handleAttemptClose = () => {
        if (mode === 'view') {
            handleClose()
        } else {
            setConfirmExitOpen(true)
        }
    }

    const fetchOccurrenceData = useCallback(async (occurrence_key) => {
        setIsLoading(true)
        try {
            let response = await axios.get(`/dash/alerts/occurrence/${occurrence_key}`)
            return response.data
        } catch (error) {
            setSnackbarContent({
                type: "error",
                message: "Ocorreu um erro ao buscar os dados da ocorrência. Por favor, tente novamente."
            })
        } finally {
            setIsLoading(false)
        }
    }, [setSnackbarContent])

    useEffect(() => {
        if (!open) return

        if (mode === "create") {
            setOccurrenceData({
                regulatory_authority: "bacen",
                external_id: "",
                start_date: null,
                end_date: null,
                branch_number: "",
                branch_name: "",
                branch_city: "",
                branch_state: "",
                credit_amount: 0,
                debit_amount: 0,
                provisioning_amount: 0,
                proposal_amount: 0,
                clause_numbers: [],
                analysis_description: "",
                communicator_document_number: "",
                involved_parties: []
            })
        } else if (occurrence) {
            const fetch = async () => {
                const data = await fetchOccurrenceData(occurrence.occurrence_key)
                if (data) {
                    const hydrated = hidrateOccurrenceDataKeys(data.occurrence_data)
                    const authority = hydrated.regulatory_authority || "bacen"
                    const isCvm = authority === "cvm"
                    setOccurrenceData({
                        ...hydrated,
                        regulatory_authority: authority,
                        involved_parties: hydrated.involved_parties?.map(party => ({
                            ...party,
                            no_document: isCvm && !(party.document_number && String(party.document_number).trim()) ? true : !!party.no_document
                        }))
                    })
                }
            }
            fetch()
        }
        setErrorState({})
    }, [open, mode, occurrence, fetchOccurrenceData, hidrateOccurrenceDataKeys])


    const getMomentDate = (date, nullCase) =>{
        if (date == null) return nullCase
        else return moment(date)
    }

    const normalizeDateValue = (date) => {
        if (!date) return date
        if (typeof date === 'string') return date
        if (moment.isMoment(date) || date instanceof Date) return moment(date).toISOString()
        return date
    }

    const handleInputChange = (field) => (event) => {
        const value = event.target.value
        setOccurrenceData((prev) => ({
            ...prev,
            [field]: value,
        }))
        if (errorState[field]) setErrorState(prev => ({...prev, [field]: null}))
    }

    const handleValueChange = (field) => (value) => {
        setOccurrenceData((prev) => ({
            ...prev,
            [field]: value,
        }))
        if (errorState[field]) setErrorState(prev => ({...prev, [field]: null}))
    }

    const handleMoneyValueChange = (field) => (value) => {
        setOccurrenceData((prev) => ({
            ...prev,
            [field]: parseInt(value.value),
        }))
    }

    const validateForm = () => {
        const errors = {}
        const data = occurrenceData
        const regulatoryAuthority = data?.regulatory_authority || "bacen"
        const isCvm = regulatoryAuthority === "cvm"

        if (!data.external_id || !data.external_id.trim()) errors.external_id = true
        if (!data.start_date) errors.start_date = true
        if (!data.end_date) errors.end_date = true

        const commDoc = (data.communicator_document_number || "").replace(/\D/g, "")
        if (!commDoc) {
            errors.communicator_document_number = t("Obrigatório")
        } else if (commDoc.length <= 11) {
            if (!validateCPF(commDoc)) errors.communicator_document_number = t("CPF Inválido")
        } else {
            if (!validateCNPJ(commDoc)) errors.communicator_document_number = t("CNPJ Inválido")
        }

        if (!isCvm) {
            if (!data.branch_number) errors.branch_number = true
            if (!data.branch_name) errors.branch_name = true
        }
        if (!data.branch_city) errors.branch_city = true
        if (!data.branch_state) errors.branch_state = true

        if (isCvm && (data.credit_amount == null || data.credit_amount === 0)) errors.credit_amount = true

        if (!data.clause_numbers || data.clause_numbers.length === 0) errors.clause_numbers = true

        if (!data.analysis_description || !data.analysis_description.trim()) errors.analysis_description = true

        const hasParties = data.involved_parties && data.involved_parties.length > 0
        const hasHolder = data?.involved_parties?.some(party => party.type === 'holder')
        if (!hasParties || !hasHolder) {
            errors.involved_parties = 'no_holder'
            setExpanded(prev => ({...prev, parties: true}))
        } else if (!isCvm && data.involved_parties.length) {
            const partyWithoutDocument = data.involved_parties.some(
                party => !(party.document_number && String(party.document_number).trim())
            )
            if (partyWithoutDocument) {
                errors.involved_parties = 'missing_document'
                setExpanded(prev => ({...prev, parties: true}))
            } else {
                const holderMissingBranchAccount = data.involved_parties.some(
                    party => party.type === 'holder' && (
                        !(party.branch_number && String(party.branch_number).trim()) ||
                        !(party.branch_name && String(party.branch_name).trim()) ||
                        !(party.account_number && String(party.account_number).trim()) ||
                        !(party.account_open_date && String(party.account_open_date).trim()) ||
                        !(party.register_update_date && String(party.register_update_date).trim())
                    )
                )
                if (holderMissingBranchAccount) {
                    errors.involved_parties = 'holder_missing_branch_or_account_info'
                    setExpanded(prev => ({...prev, parties: true}))
                }
            }
        }

        return errors
    }

    const handleSave = async () => {
        const errors = validateForm()
        if (Object.keys(errors).length > 0) {
            setErrorState(errors)
            return
        }

        setIsSaving(true)
        try {
            if (mode === 'create') {
                await axios.post(`/dash/alerts/occurrence`, {occurrence_data: sanitizeOccurrenceData(occurrenceData)})
            } else {
                await axios.put(`/dash/alerts/occurrence/${occurrence.occurrence_key}`, {occurrence_data: sanitizeOccurrenceData(occurrenceData)})
            }
            handleClose()
        } catch (error) {
            if (error.response.status === 400 && error.response.data.title === "Duplicate occurrence external_id") {
                setSnackbarContent({
                    type: "error",
                    message: "Já existe uma ocorrência com o mesmo identificador. Por favor, utilize um identificador único."
                })
            } else {
                setSnackbarContent({
                    type: "error",
                    message: "Ocorreu um erro ao salvar a ocorrência. Por favor tente novamente mais tarde."
                })
            }
        } finally {
            setIsSaving(false)
        }
    }

    const modeVerbDict = {
        create: t("Criar"),
        edit: t("Editar"),
        view: t("Visualizar"),
    }
    const modeVerb = modeVerbDict[mode] || t("Visualizar")
    const readOnly = mode === "view"
    const isCvm = occurrenceData?.regulatory_authority === "cvm"
    const isBatched = occurrence?.status === "in_batch"

    const ufList = [
            "AC","AL","AP","AM","BA","CE","DF","ES","GO","MA",
            "MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN",
            "RS","RO","RR","SC","SP","SE","TO"
          ]

    return (
        <>
        <Dialog
            open={open}
            onClose={handleAttemptClose}
            fullWidth
            maxWidth="lg"
            PaperProps={{ style: { padding: "20px 25px" } }}
        >
            <div style={{ display: "flex", marginBottom: "12px", justifyContent: "space-between" }}>
                <span className={["blueText", "titleSize"].join(" ")}>
                    {[modeVerb, t("Ocorrência")].join(' ')}
                </span>
                <IconButton onClick={handleAttemptClose}>
                    <Cancel />
                </IconButton>
            </div>
            {occurrenceData && (
                <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
                    <Typography className="blueText" style={{ fontWeight: 'bold', flexShrink: 0 }}>
                        {t("regulatory_authority")}:
                    </Typography>
                    <ToggleButtonGroup
                            value={occurrenceData?.regulatory_authority === "cvm" ? "cvm" : "bacen"}
                            exclusive
                            onChange={(_, newAuthority) => {
                                if (newAuthority == null) return
                                setOccurrenceData((prev) => ({
                                    ...prev,
                                    regulatory_authority: newAuthority,
                                    ...(prev?.regulatory_authority !== newAuthority ? { clause_numbers: [] } : {})
                                }))
                                setErrorState({})
                            }}
                            disabled={readOnly || isBatched}
                            size="small"
                            sx={{
                                backgroundColor: "rgba(11, 31, 130, 0.12)",
                                borderRadius: "50px",
                                padding: "4px",
                                "& .MuiToggleButtonGroup-grouped": {
                                    border: "none",
                                    "&:not(:first-of-type)": { border: "none", marginLeft: 0 },
                                },
                                "& .MuiToggleButton-root": {
                                    borderRadius: "50px",
                                    border: "none",
                                    color: "#0b1f82",
                                    fontWeight: 500,
                                    minWidth: "72px",
                                    transition: "background-color 200ms ease-in-out, color 200ms ease-in-out",
                                    "&.Mui-selected": {
                                        backgroundColor: "#0b1f82",
                                        color: "#fff",
                                        "&:hover": { backgroundColor: "#0b1f82" },
                                    },
                                    "&:not(.Mui-selected):hover": {
                                        backgroundColor: "rgba(11, 31, 130, 0.08)",
                                    },
                                },
                                "& .MuiToggleButton-root.Mui-disabled": {
                                    border: "none !important",
                                    outline: "none !important",
                                    boxShadow: "none !important",
                                },
                                "& .MuiToggleButton-root.Mui-disabled:not(.Mui-selected)": {
                                    color: "rgba(0, 0, 0, 0.38)",
                                },
                            }}
                        >
                            <ToggleButton value="bacen" disableRipple>{t("BACEN")}</ToggleButton>
                            <ToggleButton value="cvm" disableRipple>{t("CVM")}</ToggleButton>
                        </ToggleButtonGroup>
                </div>
            )}
            {isLoading && (
                <div style={{ width: "auto" }}>
                    <div className='circularProgressCenter'>
                        <CircularProgress />
                    </div>
                </div>
            )}
            {!isLoading && (
            <div>
                <Accordion
                    expanded={expanded?.parties}
                    onChange={(_, isExpanded) => setExpanded((prev) => ({...prev, parties: isExpanded}))}
                    style={{ border: errorState.involved_parties ? "1px solid red" : "none" }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography className="blueText" style={{ fontWeight: 'bold', color: errorState.involved_parties ? "red" : "#0b1f82" }}>
                            {t("Partes Envolvidas")} {errorState.involved_parties === 'no_holder' ? `(${t("Obrigatório adicionar pelo menos uma parte titular")})` : errorState.involved_parties === 'missing_document' ? `(${t("involved_parties_missing_document")})` : errorState.involved_parties === 'holder_missing_branch_or_account_info' ? `(${t("involved_parties_holder_missing_branch_or_account_info")})` : ""}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <OccurrenceInvolvedPartiesTable
                            involvedParties={occurrenceData?.involved_parties}
                            onChange={handleValueChange('involved_parties')}
                            readOnly={readOnly}
                            regulatoryAuthority={occurrenceData?.regulatory_authority ?? "bacen"}
                        />
                    </AccordionDetails>

                </Accordion>
                <SectionHeader>
                    <Typography className="blueText" style={{ fontWeight: 'bold' }}>
                        {t("Dados da Ocorrência")}
                    </Typography>
                </SectionHeader>
                <div style={{ display: "flex", justifyContent: "center" }}>
                    <div style={{width: "80%", display: "flex", flexDirection: "column", alignContent: "left"}}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                            <SectionHeader style={{ minHeight: '36px'}}>
                                <Typography className="blueText">
                                    {t("Informações Gerais")}
                                </Typography>
                            </SectionHeader>
                            <TextField
                                label={t("Nome da Ocorrência")}
                                value={occurrenceData?.external_id || ""}
                                onChange={handleInputChange('external_id')}
                                InputProps={{
                                    readOnly: readOnly,
                                }}
                                error={!!errorState.external_id}
                                helperText={errorState.external_id ? t("Campo obrigatório") : ""}
                            />
                            <div style={{ display: "flex", justifyContent: "space-between", gap: "10px"}}>
                                <div style={{width: '50%'}}>
                                    <CoafDateRangePicker
                                        label={t("Período (Início - Fim)")}
                                        startDate={getMomentDate(occurrenceData?.start_date, null)}
                                        endDate={getMomentDate(occurrenceData?.end_date, null)}
                                        onDatesChange={({startDate, endDate}) => {
                                            setOccurrenceData(prev => ({
                                                ...prev,
                                                start_date: normalizeDateValue(startDate),
                                                end_date: normalizeDateValue(endDate)
                                            }))
                                            if(errorState.start_date) setErrorState(prev => ({...prev, start_date: null}))
                                            if(errorState.end_date) setErrorState(prev => ({...prev, end_date: null}))
                                        }}
                                        readOnly={readOnly}
                                        error={!!errorState.start_date || !!errorState.end_date}
                                    />
                                    {errorState.start_date && <span style={{color: 'red', fontSize: '12px'}}>{t("Data inicial obrigatória")}</span>}
                                </div>
                                <Tooltip title={errorState.communicator_document_number || ""} open={!!errorState.communicator_document_number} placement="top" arrow>
                                    <div style={{width: '50%'}}>
                                        <NumberFormat
                                            customInput={TextField}
                                            label={t("Documento do Comunicador")}
                                            placeholder="CPF/CNPJ"
                                            value={occurrenceData?.communicator_document_number}
                                            format={
                                                occurrenceData?.communicator_document_number?.replace(/\D/g, "").length > 11 ? "##.###.###/####-##" : "###.###.###-#####"
                                            }
                                            onValueChange={(values) => {
                                                setOccurrenceData(prev => ({ ...prev, communicator_document_number: values.formattedValue }))
                                                if(errorState.communicator_document_number) setErrorState(prev => ({...prev, communicator_document_number: null}))
                                            }}
                                            error={!!errorState.communicator_document_number}
                                            InputProps={{ readOnly: readOnly }}
                                            fullWidth
                                        />
                                    </div>
                                </Tooltip>
                            </div>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: "5px"}}>
                            <SectionHeader style={{ minHeight: '36px'}}>
                                <Typography className="blueText">
                                    {t("Informações da Agência")}
                                </Typography>
                            </SectionHeader>
                            <div style={{ display: "flex", justifyContent: "space-between", gap: "10px"}}>
                                <NumberFormat
                                    value={isCvm ? "" : (occurrenceData?.branch_number ?? "")}
                                    customInput={TextField}
                                    allowNegative={false}
                                    thousandSeparator={""}
                                    onValueChange={(values) => {
                                        if (!isCvm) {
                                            setOccurrenceData(prev => ({ ...prev, branch_number: values.value }))
                                            if(errorState.branch_number) setErrorState(prev => ({...prev, branch_number: null}))
                                        }
                                    }}
                                    placeholder={"0000"}
                                    decimalScale={0}
                                    fixedDecimalScale={true}
                                    label={t("Nº da Agência")}
                                    InputLabelProps={{ shrink: true, readOnly: readOnly}}
                                    inputProps={{readOnly: readOnly}}
                                    allowLeadingZeros={true}
                                    format={"####"}
                                    onBlur={() => {
                                        if (occurrenceData.branch_number) {
                                            setOccurrenceData(prev => ({ ...prev, branch_number: occurrenceData.branch_number.padStart(4, "0") }))
                                        }
                                    }}
                                    readOnly={readOnly}
                                    disabled={isCvm}
                                    fullWidth
                                    style={{ width: "25%" }}
                                    error={!!errorState.branch_number}
                                />
                                <TextField
                                    label={t("Nome da Agência")}
                                    value={isCvm ? "" : (occurrenceData?.branch_name || "")}
                                    onChange={handleInputChange('branch_name')}
                                    disabled={isCvm}
                                    InputProps={{
                                        readOnly: readOnly,
                                    }}
                                    fullWidth
                                    style={{ flex: 1}}
                                    error={!!errorState.branch_name}
                                />
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", gap: "10px"}}>
                                <TextField
                                    label={t("Cidade da Agência")}
                                    value={occurrenceData?.branch_city || ""}
                                    onChange={handleInputChange('branch_city')}
                                    InputProps={{
                                        readOnly: readOnly,
                                    }}
                                    fullWidth
                                    style={{ flex: 1}}
                                    error={!!errorState.branch_city}
                                />
                                <Autocomplete
                                    options={ufList}
                                    value={occurrenceData?.branch_state || null}
                                    onChange={(event, newValue) => handleValueChange('branch_state')(newValue)}
                                    readOnly={readOnly}
                                    renderInput={(params) => (
                                         <TextField
                                            {...params}
                                            label={t("UF")}
                                            InputProps={{
                                                ...params.InputProps,
                                                readOnly: readOnly,
                                            }}
                                            style={{ bottom: "-4px" }}
                                            className="filterTextbox"
                                            error={!!errorState.branch_state}
                                        />
                                    )}
                                    fullWidth
                                    style={{ width: "18%" }}
                                />
                            </div>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: "5px"}}>
                            <SectionHeader style={{ minHeight: '36px'}}>
                                <Typography className="blueText">
                                    {t("Valores")}
                                </Typography>
                            </SectionHeader>
                            <div style={{ display: "flex", gap: "24px", flexWrap: "wrap", rowGap: "10px"}}>
                                <div>
                                    <CoafMoneyTextField
                                        label={t("Valor Creditado")}
                                        value={occurrenceData?.credit_amount ?? 0}
                                        onChange={(val) => {
                                            handleMoneyValueChange('credit_amount')(val)
                                            if (errorState.credit_amount) setErrorState((prev) => ({ ...prev, credit_amount: null }))
                                        }}
                                        readOnly={readOnly}
                                    />
                                </div>
                                <CoafMoneyTextField
                                    label={t("Valor Debitado")}
                                    value={isCvm ? 0 : (occurrenceData?.debit_amount ?? 0)}
                                    onChange={(val) => {
                                        if (!isCvm) handleMoneyValueChange('debit_amount')(val)
                                    }}
                                    readOnly={readOnly}
                                    disabled={isCvm}
                                />
                                <CoafMoneyTextField
                                    label={t("Valor Provisionado")}
                                    value={isCvm ? 0 : (occurrenceData?.provisioning_amount ?? 0)}
                                    onChange={(val) => {
                                        if (!isCvm) handleMoneyValueChange('provisioning_amount')(val)
                                    }}
                                    readOnly={readOnly}
                                    disabled={isCvm}
                                />
                                <CoafMoneyTextField
                                    label={t("Valor Proposto")}
                                    value={isCvm ? 0 : (occurrenceData?.proposal_amount ?? 0)}
                                    onChange={(val) => {
                                        if (!isCvm) handleMoneyValueChange('proposal_amount')(val)
                                    }}
                                    readOnly={readOnly}
                                    disabled={isCvm}
                                />
                            </div>
                            <div>
                                <SectionHeader style={{ minHeight: '36px'}}>
                                    <Typography className="blueText">
                                        {t("Detalhamento da Ocorrência")}
                                    </Typography>
                                </SectionHeader>
                                <div style={{ border: errorState.clause_numbers ? "1px solid red" : "none", borderRadius: "4px" }}>
                                    <CoafClauseNumberList
                                            value={occurrenceData?.clause_numbers}
                                            onChange={(val) => {
                                                handleValueChange('clause_numbers')(val)
                                                if(val && val.length > 0) setErrorState(prev => ({...prev, clause_numbers: null}))
                                            }}
                                            readOnly={readOnly}
                                            regulatoryAuthority={occurrenceData?.regulatory_authority ?? "bacen"}
                                    />
                                    {errorState.clause_numbers && <span style={{color: 'red', fontSize: '12px', marginLeft: '10px'}}>{t("Selecione ao menos um enquadramento")}</span>}
                                </div>
                                <TextField
                                    label={t("Descrição")}
                                    value={occurrenceData?.analysis_description || ""}
                                    onChange={handleInputChange('analysis_description')}
                                    InputProps={{
                                        readOnly: readOnly,
                                    }}
                                    error={!!errorState.analysis_description}
                                    helperText={errorState.analysis_description ? t("Campo obrigatório") : ""}
                                    multiline
                                    minRows={4}
                                    fullWidth
                                    style={{ marginTop: "10px" }}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {!readOnly && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '30px' }}>
                        <Button
                            variant="contained"
                            className={["button", "standard", "normalText", "normalMediumSize"].join(" ")}
                            style={{ minWidth: '150px', borderRadius: '18px' }}
                            onClick={handleSave}
                            disabled={isSaving}
                        >
                            {isSaving ? <CircularProgress size={24} color="inherit" /> : t("Confirmar dados")}
                        </Button>
                    </div>
                )}
            </div>
            )}
        </Dialog>
        {confirmExitOpen && <Dialog
            open={confirmExitOpen}
            onClose={() => setConfirmExitOpen(false)}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
            <DialogTitle id="alert-dialog-title" style={{ textAlign: "center", color: "#0b1f82", fontWeight: "bold" }}>
                {t("Tem certeza que deseja fechar?")}
            </DialogTitle>
            <DialogContent>
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        textAlign: "center",
                        gap: "20px"
                    }}
                >
                    <div className="internalCardContainer normalText dialogInfoSize" id="alert-dialog-description" style={{ fontWeight: "normal", textAlign: "justify" }}>
                        {t("Você perderá os dados novos/alterados. Deseja continuar?")}
                    </div>
                    <div style={{ display: "flex", gap: "15px" }}>
                        <Button
                            onClick={() => setConfirmExitOpen(false)}
                            variant="outlined"
                            sx={{
                                color: "#0b1f82",
                                borderColor: "#0b1f82",
                                "&:hover": { borderColor: "#0b1f82", backgroundColor: "rgba(11, 31, 130, 0.08)" }
                            }}
                        >
                            {t("Não, continuar editando")}
                        </Button>
                        <Button
                            onClick={handleClose}
                            variant="contained"
                            sx={{
                                backgroundColor: "#0b1f82",
                                "&:hover": { backgroundColor: "#0b1f82" }
                            }}
                        >
                            {t("Sim, fechar")}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>}
        </>
    )
}

export default CoafOccurrenceDialog